import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { BrandProvider, AuthProvider, CartProvider, useAuth, useCart, useBrand } from './context/AppContext';
import { Toasts } from './components/UI';
import { logout } from './api';

import LoginScreen    from './screens/LoginScreen';
import ProductsScreen from './screens/ProductsScreen';
import AdminScreen    from './screens/AdminScreen';
import PSPScreen      from './screens/PSPScreen';
import TermsScreen    from './screens/TermsScreen';
import PrivacyScreen  from './screens/PrivacyScreen';
import { ItemDetailScreen, CartScreen, FinalizeScreen, PendingScreen, UploadScreen, ResourcesScreen } from './screens/Screens';
import SamplesScreen from './screens/SamplesScreen';

function CartBadge() {
  const { cartCount } = useCart();
  if (!cartCount) return null;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:18, height:18, borderRadius:'50%', background:'rgba(255,255,255,.9)', color:'var(--brand)', fontSize:10, fontWeight:700, marginLeft:5 }}>
      {cartCount}
    </span>
  );
}

function AdminNav() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return null;
  return (
    <nav className="nav admin-nav">
      <span className="nav-logo">Solidiphy Admin</span>
      <div className="nav-links">
        <span className="nav-group-badge">Admin</span>
        <button className="nav-link" onClick={async () => {
          await logout().catch(() => {});
          localStorage.removeItem('solidiphy_brand');
          window.location.href = '/admin';
        }}>Sign out</button>
      </div>
    </nav>
  );
}

function Nav() {
  const { authenticated, group } = useAuth();
  const { brandName, brandLogo } = useBrand();

  const handleLogout = async () => {
    await logout().catch(() => {});
    localStorage.removeItem('solidiphy_brand');
    sessionStorage.removeItem('solidiphy_cart'); // window.location.href below is a hard navigation — React never sees `authenticated` flip false, so the cleanup effect in CartProvider doesn't get a chance to run
    window.location.href = '/login';
  };

  if (!authenticated) return null;

  return (
    <nav className="nav">
      <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0, overflow:'hidden' }}>
        <NavLink to="/products" className="nav-logo">
          {brandLogo
            ? <img src={brandLogo} alt={brandName} style={{ height:32, objectFit:'contain' }} />
            : <>
                <div className="nav-logo-mark">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="1" width="6" height="6" rx="1" fill="white"/>
                    <rect x="9" y="1" width="6" height="6" rx="1" fill="white"/>
                    <rect x="1" y="9" width="6" height="6" rx="1" fill="white"/>
                    <rect x="9" y="9" width="6" height="6" rx="1" fill="white"/>
                  </svg>
                </div>
                {brandName}
              </>
          }
        </NavLink>
        {group?.name && (
          <span className="nav-group-badge" style={{ whiteSpace:'nowrap' }}>{group.name}</span>
        )}
        {group?.location && (
          <span style={{ fontSize:11, color:'rgba(255,255,255,.7)', whiteSpace:'nowrap' }}>📍 {group.location}</span>
        )}
      </div>
      <div className="nav-links">
        <NavLink to="/products"  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Products</NavLink>
        <NavLink to="/cart"      className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Cart<CartBadge /></NavLink>
        <NavLink to="/pending"   className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Pending Sales</NavLink>
        <NavLink to="/samples"   className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Samples</NavLink>
        <NavLink to="/resources" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Resources</NavLink>
        <button className="nav-link" onClick={handleLogout} style={{ fontSize:12, color:'rgba(255,255,255,.7)' }}>
          Sign out
        </button>
      </div>
    </nav>
  );
}

function RequireAuth({ children }) {
  const { checked, authenticated } = useAuth();
  if (!checked) return null;
  return authenticated ? children : <Navigate to="/login" replace />;
}

function Footer() {
  const { authenticated, isAdmin } = useAuth();
  if (!authenticated || isAdmin) return null;
  return (
    <footer style={{ borderTop:'1px solid var(--border)', padding:'12px 24px', textAlign:'center', fontSize:11, color:'var(--text3)', background:'var(--surface)' }}>
      <a href="/terms"   target="_blank" style={{ color:'var(--text3)', textDecoration:'underline' }}>Terms & Conditions</a>
      <span style={{ margin:'0 8px' }}>·</span>
      <a href="/privacy" target="_blank" style={{ color:'var(--text3)', textDecoration:'underline' }}>Privacy Statement</a>
      <span style={{ margin:'0 8px' }}>·</span>
      <span>© {new Date().getFullYear()} Solidiphy</span>
    </footer>
  );
}

function AppShell() {
  const isAdminPage = window.location.pathname.startsWith('/admin');
  const isPSPPage   = window.location.pathname.startsWith('/psp');
  return (
    <div className="shell">
      {!isAdminPage && !isPSPPage && <Nav />}
      {isAdminPage && <AdminNav />}
      <Routes>
        <Route path="/login"       element={<LoginScreen />} />
        <Route path="/admin"       element={<AdminScreen />} />
        <Route path="/psp"         element={<PSPScreen />} />
        <Route path="/psp/*"       element={<PSPScreen />} />
        <Route path="/terms"       element={<TermsScreen />} />
        <Route path="/privacy"     element={<PrivacyScreen />} />
        <Route path="/products"    element={<RequireAuth><ProductsScreen /></RequireAuth>} />
        <Route path="/product/:id" element={<RequireAuth><ItemDetailScreen /></RequireAuth>} />
        <Route path="/cart"        element={<RequireAuth><CartScreen /></RequireAuth>} />
        <Route path="/finalize"    element={<RequireAuth><FinalizeScreen /></RequireAuth>} />
        <Route path="/pending"     element={<RequireAuth><PendingScreen /></RequireAuth>} />
        <Route path="/upload/:id"  element={<RequireAuth><UploadScreen /></RequireAuth>} />
        <Route path="/resources" element={<RequireAuth><ResourcesScreen /></RequireAuth>} />
        <Route path="/samples"   element={<RequireAuth><SamplesScreen /></RequireAuth>} />
        <Route path="*"            element={<Navigate to="/products" replace />} />
      </Routes>
      {!isPSPPage && <Footer />}
      <Toasts />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <BrandProvider>
        <AuthProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </AuthProvider>
      </BrandProvider>
    </BrowserRouter>
  );
}
